import { getColWidth } from '@modules/dashboard/components/Layout/helpers/getColWidth';
import { MIN_CELL_COL_SPAN, ROW_COL_SPAN } from '@modules/dashboard/constants';
import { DashboardCell } from 'generated/graphql';
import { cloneDeep, throttle } from 'lodash';

interface RowCellsResizeControllerParams {
  rowCells: DashboardCell[];
  onResize: (rowCells: DashboardCell[]) => void;
  onResizeEnd: (rowCells: DashboardCell[]) => void;
  rowWidth: number;
}

export class RowCellsResizeController {
  private cellResizerXPosition: number = 0;
  private rowWidth = 0;
  private originalRowCells: DashboardCell[] = [];
  private rowCells: DashboardCell[] = [];
  private resizerIdx: number = -1;
  private onResize: (rowCells: DashboardCell[]) => void = () => {};
  private onResizeEnd: (rowCells: DashboardCell[]) => void = () => {};

  constructor({
    rowCells,
    onResize,
    rowWidth,
    onResizeEnd,
  }: RowCellsResizeControllerParams) {
    this.resetRowCells(rowCells);
    this.onResize = onResize;
    this.onResizeEnd = onResizeEnd;
    this.rowWidth = rowWidth;
  }

  resetRowCells(rowCells: DashboardCell[]) {
    this.originalRowCells = rowCells;
    this.rowCells = cloneDeep(rowCells);
  }

  private getTotalColsTaken(start: number, end: number) {
    let totalColTaken = 0;
    const cells = [...this.rowCells];

    for (let i = start; i < end; i++) {
      totalColTaken += cells[i].columnSpan;
    }

    return totalColTaken;
  }

  private getShrinkableColNextToResizer(resizerIdx: number) {
    const numCellsToLeft = resizerIdx;
    const numCellsToRight = this.rowCells.length - numCellsToLeft;
    const totalColOnLeft = this.getTotalColsTaken(0, numCellsToLeft);
    const totalColOnRight = ROW_COL_SPAN - totalColOnLeft;

    const shrinkableColOnLeft =
      totalColOnLeft - numCellsToLeft * MIN_CELL_COL_SPAN;
    const shrinkableColOnRight =
      totalColOnRight - numCellsToRight * MIN_CELL_COL_SPAN;

    return { shrinkableColOnLeft, shrinkableColOnRight };
  }

  private canResizeCellsNextToResizer(
    resizeIndicatorIdx: number,
    resizeDelta = 0,
  ) {
    // we check that all the cells are loaded because otherwise we could potentially try to resize a cell that still doesn't exist
    if (!resizeIndicatorIdx || resizeIndicatorIdx >= this.rowCells.length) {
      return false;
    }

    const { shrinkableColOnLeft, shrinkableColOnRight } =
      this.getShrinkableColNextToResizer(resizeIndicatorIdx);

    if (resizeDelta > 0) {
      //check to see if there is enough extra cols among cells to shrink them by delta
      return resizeDelta <= shrinkableColOnRight;
    } else {
      return Math.abs(resizeDelta) <= shrinkableColOnLeft;
    }
  }

  private cellResizeHandler = throttle((event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const cellItems = this.rowCells;
    const delta =
      (event.clientX - this.cellResizerXPosition) / getColWidth(this.rowWidth);

    let absDelta = Math.abs(delta);

    this.cellResizerXPosition = event.clientX;

    function resizeCellsByAbsDelta(
      shrinkingCellIdx: number,
      expandingCellIdx: number,
    ) {
      if (cellItems[shrinkingCellIdx].columnSpan <= MIN_CELL_COL_SPAN) {
        return;
      }
      const cellShrinkableColSpan = Math.min(
        absDelta,
        cellItems[shrinkingCellIdx].columnSpan - MIN_CELL_COL_SPAN,
      );
      cellItems[shrinkingCellIdx].columnSpan -= cellShrinkableColSpan;
      cellItems[expandingCellIdx].columnSpan += cellShrinkableColSpan;
      absDelta -= cellShrinkableColSpan;
    }

    if (this.canResizeCellsNextToResizer(this.resizerIdx, delta)) {
      if (delta < 0) {
        const expandingCellIdx = this.resizerIdx;
        for (let i = this.resizerIdx - 1; i >= 0; i--) {
          resizeCellsByAbsDelta(i, expandingCellIdx);
          if (!absDelta) {
            break;
          }
        }
      } else {
        const expandingCellIdx = this.resizerIdx - 1;
        for (let i = this.resizerIdx; i < cellItems.length; i++) {
          resizeCellsByAbsDelta(i, expandingCellIdx);
          if (!absDelta) {
            break;
          }
        }
      }

      this.onResize(this.rowCells);
    }
  }, 50);

  disconnect() {
    window.removeEventListener('mousemove', this.cellResizeHandler);
    window.removeEventListener('mouseup', this.cellResizeStopHandler);
  }

  private cellResizeStopHandler = () => {
    this.disconnect();

    const cellItems = this.rowCells;
    cellItems.forEach((item) => {
      item.columnSpan = Math.round(item.columnSpan);
    });

    this.cellResizerXPosition = 0;
    this.resetRowCells(this.rowCells);
    this.onResize(this.rowCells);

    //if total col taken after rounding colSpan is not 12, revert changes
    if (this.getTotalColsTaken(0, cellItems.length) !== ROW_COL_SPAN) {
      this.resetRowCells(this.originalRowCells);
      this.onResize(this.originalRowCells);
    }

    this.resizerIdx = -1;
    setTimeout(() => {
      this.onResizeEnd(this.rowCells);
    }, 10);
  };

  private registerMouseEvents() {
    window.addEventListener('mousemove', this.cellResizeHandler);
    window.addEventListener('mouseup', this.cellResizeStopHandler);
  }

  startResizingCellWidth = (event: MouseEvent, resizerIdx: number) => {
    event.preventDefault();
    event.stopPropagation();

    this.resizerIdx = resizerIdx;
    this.cellResizerXPosition = event.clientX;

    this.registerMouseEvents();
  };

  setRowWidth(width: number) {
    this.rowWidth = width;
  }

  getResizeIndex(): number {
    return this.resizerIdx;
  }

  getVisiblePreviewIndicators() {
    const totalColumnOnLeft = this.getTotalColsTaken(0, this.resizerIdx);
    const nearestPreviewIndicator = Math.round(totalColumnOnLeft);

    const { shrinkableColOnLeft, shrinkableColOnRight } =
      this.getShrinkableColNextToResizer(this.resizerIdx);

    const firstVisiblePreviewIndicator = Math.round(
      nearestPreviewIndicator - shrinkableColOnLeft,
    );
    const lastVisiblePreviewIndicator = Math.round(
      shrinkableColOnRight + nearestPreviewIndicator,
    );

    return { firstVisiblePreviewIndicator, lastVisiblePreviewIndicator };
  }
}
